import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import Layout from './components/Layout'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Students from './pages/Students'
import StudentsV2 from './pages/StudentsV2'
import Attendance from './pages/Attendance'
import BuildingBlocks from './pages/BuildingBlocks'
import LearningPlans from './pages/LearningPlans'
import Tasks from './pages/Tasks'
import Settings from './pages/Settings'
import DeviceManager from './pages/DeviceManager'
import Lernbuero from './pages/Lernbuero'
import Statistics from './pages/Statistics'
import Sitzordnung from './pages/Sitzordnung'
import PaymentManager from './components/PaymentManager'
import License from './pages/License'
import { useEffect, useRef, useState } from 'react'
import { api } from './lib/api'
import toast from 'react-hot-toast'
import Curricula from './pages/Curricula'
import CurriculumEditor from './pages/CurriculumEditor'
import { JobQueue } from './pages/JobQueue'
import Courses from './pages/Courses'
import Classes from './pages/Classes'
import Groups from './pages/Groups'
import Units from './pages/Units'
import Massenmail from './pages/Massenmail'
import MassenmailEditor from './pages/MassenmailEditor'
import ElternbriefPublic from './pages/ElternbriefPublic'
import Klassenbuch from './pages/Klassenbuch'
import KlassenbuchAckPublic from './pages/KlassenbuchAckPublic'
import StudentKlassenbuch from './pages/student/StudentKlassenbuch'
import NamenLernen from './pages/NamenLernen'
import NamenLernenQuiz from './pages/NamenLernenQuiz'
import NamenLernenSnake from './pages/NamenLernenSnake'
import FotoOptimierung from './pages/FotoOptimierung'
import Unterrichten from './pages/Unterrichten'
import LiveLessonPage from './pages/LiveLesson'
import ClassworkPage from './pages/Classwork'
import PerformanceDashboard from './pages/PerformanceDashboard'
import AdminHubKontostand from './pages/AdminHubKontostand'
import AdminAcousticCalibration from './pages/AdminAcousticCalibration'
import UiShowcase from './pages/UiShowcase'
import Timetables from './pages/Timetables'
import TimetableViewer from './pages/TimetableViewer'
import StudentDashboard from './pages/student/StudentDashboard'
import StudentLearningPlans from './pages/student/StudentLearningPlans'
import StudentSkillmap from './pages/student/StudentSkillmap'
import StudentClassworkPage from './pages/student/Classwork'
import Appointments from './pages/Appointments'
import AppointmentsPublic from './pages/AppointmentsPublic'
import AppointmentsAll from './pages/AppointmentsAll'
import AppointmentsPublicEvent from './pages/AppointmentsPublicEvent'
import AppointmentConfirm from './pages/AppointmentConfirm'
import AppointmentCancel from './pages/AppointmentCancel'
import AppointmentConfirmCancel from './pages/AppointmentConfirmCancel'
import AppointmentCancelAll from './pages/AppointmentCancelAll'
import AppointmentConfirmCancelAll from './pages/AppointmentConfirmCancelAll'
import AppointmentConfirmBatch from './pages/AppointmentConfirmBatch'
import StudentAppointments from './pages/student/StudentAppointments'
import StudentFilesQueue from './pages/StudentFilesQueue'
import EmailClient from './pages/EmailClient'
import StudentFiles from './pages/StudentFiles'
import StudentFilesReviewDashboard from './pages/StudentFilesReviewDashboard'
import Dateien from './pages/Dateien'
import Display from './pages/Display'
import DisplayMirrorPage from './pages/DisplayMirrorPage'
import StudentAssessments from './pages/StudentAssessments'
import {
  canAccessMenuKey,
  NavigationMenuKey,
  NavigationPermissionMatrix,
  NAVIGATION_PERMISSION_UPDATED_EVENT,
  readMatrixFromStorage,
  sanitizeNavigationPermissionMatrix,
  writeMatrixToStorage
} from './lib/navigationPolicy'
import {
  applyUiPreferencesToDocument,
  getThemePreference,
  subscribeSystemTheme
} from './lib/lrUiPreferences'
import MfaSetupWizard from './components/auth/MfaSetupWizard'

// Protected Route Component for Admin-only access
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  
  // Check if user has admin role in any of their roles
  const userRoles = user?.allRoles || user?.roles || [user?.role]
  const isAdminRole = userRoles.some(role => 
    role?.toLowerCase() === 'admin' || 
    role?.toLowerCase() === 'administrator'
  ) || user?.username === 'admin'
  
  if (!isAdminRole) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

/** Admin, Administrator oder Schulleitung (Rollen aus /auth/me, inkl. Zusatzrollen). */
const AdminOrSchulleitungRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const userRoles = user?.allRoles || user?.roles || [user?.role]
  const ok =
    user?.username === 'admin' ||
    userRoles.some((role) => {
      const r = String(role || '').toLowerCase()
      return r === 'admin' || r === 'administrator' || r === 'schulleitung'
    })
  if (!ok) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

// Rollen, die Zugriff auf Lehrer-Bereiche erlauben (bei Mehrfachrollen z. B. Lehrer+Schüler)
const TEACHER_ACCESS_ROLES = ['teacher', 'lehrer', 'klassenlehrer', 'abteilungsleiter', 'schulleitung', 'admin', 'administrator', 'guest']

// Protected Route Component for all non-student users (teachers, admins, etc.)
const TeacherRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />

  const userRoles = (user.allRoles || user.roles || [user.role]).filter(Boolean).map((r: string) => String(r).toLowerCase())
  const hasTeacherAccess = userRoles.some((r: string) => TEACHER_ACCESS_ROLES.includes(r))

  if (!hasTeacherAccess) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Protected Route Component for Student-only access
const StudentRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const userRoles = user?.allRoles || user?.roles || [user?.role]
  const isStudent = userRoles.some(role => role?.toLowerCase() === 'student')
  if (!isStudent) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

function App() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const registeredDevicesRef = useRef<Set<string>>(new Set())
  const [navigationPermissionMatrix, setNavigationPermissionMatrix] = useState<NavigationPermissionMatrix>(() => readMatrixFromStorage())

  // Check if this is a public route BEFORE calling useAuth
  // Exclude /appointments/student as it's a protected route
  const pathname = window.location.pathname;
  const isPublicRoute = pathname.startsWith('/elternbrief/') || 
                        pathname.startsWith('/klassenbuch/ack/') ||
                        (pathname.startsWith('/appointments/') && 
                         !pathname.startsWith('/appointments/student')) ||
                        pathname === '/display'
  
  // Always call useAuth (React Hook rules), but handle public routes specially
  const { isAuthenticated, isLoading, user, logout } = useAuth()
  
  // For public routes, override auth state to prevent redirects
  const authResult = isPublicRoute ? { isAuthenticated: true, isLoading: false, user: null } : { isAuthenticated, isLoading, user }

  useEffect(() => {
    let mounted = true
    const loadNavigationPermissions = async () => {
      try {
        const res = await api.get('/settings/navigation-permissions')
        if (!mounted || !res?.data?.success) return
        const matrix = sanitizeNavigationPermissionMatrix((res.data.data?.matrix || {}) as NavigationPermissionMatrix)
        setNavigationPermissionMatrix(matrix)
        writeMatrixToStorage(matrix)
      } catch {
        // Keep matrix fallback from local storage
      }
    }

    if (authResult.isAuthenticated && !isPublicRoute) {
      void loadNavigationPermissions()
    }

    return () => {
      mounted = false
    }
  }, [authResult.isAuthenticated, isPublicRoute, user?.username])

  useEffect(() => {
    const syncMatrixFromStorage = () => {
      setNavigationPermissionMatrix(readMatrixFromStorage())
    }

    const handleCustomMatrixUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ matrix?: NavigationPermissionMatrix }>
      if (customEvent.detail?.matrix) {
        setNavigationPermissionMatrix(customEvent.detail.matrix)
        return
      }
      syncMatrixFromStorage()
    }

    window.addEventListener('storage', syncMatrixFromStorage)
    window.addEventListener(NAVIGATION_PERMISSION_UPDATED_EVENT, handleCustomMatrixUpdate as EventListener)
    return () => {
      window.removeEventListener('storage', syncMatrixFromStorage)
      window.removeEventListener(NAVIGATION_PERMISSION_UPDATED_EVENT, handleCustomMatrixUpdate as EventListener)
    }
  }, [])

  useEffect(() => {
    if (getThemePreference() !== 'system') return
    return subscribeSystemTheme(() => applyUiPreferencesToDocument())
  }, [])

  // Function to auto-register device after successful login
  const autoRegisterDevice = async (deviceId: string) => {
    try {
      console.log('🔧 Auto-registering device:', deviceId)
      const response = await api.post('/nfc-daemons/auto-register', { deviceCode: deviceId })
      
      if (response.data.success) {
        const { message, ownershipTransferred, previousOwner } = response.data
        
        if (ownershipTransferred && previousOwner) {
          toast.success(`✅ ${message}`, {
            duration: 5000,
            icon: '🔄'
          })
          console.log(`✅ Device ownership transferred from ${previousOwner} to current user`)
        } else {
          toast.success(`✅ ${message}`)
          console.log('✅ Device auto-registered successfully')
        }
        
        // Remove the device ID from URL parameters after successful registration
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('GeraeteID')
        const newUrl = `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`
        navigate(newUrl, { replace: true })
      }
    } catch (error: any) {
      console.error('❌ Error auto-registering device:', error)
      const message = error.response?.data?.message || 'Fehler bei der automatischen Geräteregistrierung'
      toast.error(message)
      // Remove device from processed set on error to allow retry
      registeredDevicesRef.current.delete(deviceId)
    }
  }

  // Check for device ID in URL parameters when user is already authenticated
  useEffect(() => {
    if (authResult.isAuthenticated && !isPublicRoute) {
      const deviceId = searchParams.get('GeraeteID')
      if (deviceId && !registeredDevicesRef.current.has(deviceId)) {
        console.log('🔍 Device ID found in URL for authenticated user:', deviceId)
        // Mark device as being processed to prevent duplicate registrations
        registeredDevicesRef.current.add(deviceId)
        // Auto-register the device immediately since user is already logged in
        autoRegisterDevice(deviceId)
      }
    }
  }, [authResult.isAuthenticated, searchParams, isPublicRoute])

  // Public routes (no authentication required) - MUST be checked FIRST
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/elternbrief/:token" element={<ElternbriefPublic />} />
        <Route path="/klassenbuch/ack/:token" element={<KlassenbuchAckPublic />} />
        <Route path="/appointments/all" element={<AppointmentsAll />} />
        <Route path="/appointments/event/:token" element={<AppointmentsPublicEvent />} />
        <Route path="/appointments/:token" element={<AppointmentsPublic />} />
        <Route path="/appointments/confirm/:token" element={<AppointmentConfirm />} />
        <Route path="/appointments/confirm-batch/:groupToken" element={<AppointmentConfirmBatch />} />
        <Route path="/appointments/cancel/:token" element={<AppointmentCancel />} />
        <Route path="/appointments/confirm-cancel/:token" element={<AppointmentConfirmCancel />} />
        <Route path="/appointments/cancel-all/:groupToken" element={<AppointmentCancelAll />} />
        <Route path="/appointments/confirm-cancel-all/:token" element={<AppointmentConfirmCancelAll />} />
        <Route path="/display" element={<Display />} />
      </Routes>
    )
  }

  // Only check authentication for protected routes
  if (authResult.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!authResult.isAuthenticated) {
    return <Login />
  }

  // Defensive frontend guard: Schüler nie in Lehrer-Routen lassen.
  const studentAllowed = new Set(['/Schüler_Dashboard', '/Meine Lernpläne', '/Skillmap', '/appointments/student', '/dateien', '/student/classwork', '/student/klassenbuch'])
  const currentPath = decodeURIComponent(window.location.pathname)
  const rolesForGuard = (user?.allRoles || user?.roles || [user?.role] || []).filter(Boolean).map((r: string | undefined) => String(r ?? '').toLowerCase())
  const hasTeacherRole = rolesForGuard.some((r: string) => TEACHER_ACCESS_ROLES.includes(r))
  const isOnlyStudent = rolesForGuard.length > 0 && !hasTeacherRole

  if (isOnlyStudent && !studentAllowed.has(currentPath)) {
    return <Navigate to="/Schüler_Dashboard" replace />
  }

  const canAccessRouteMenu = (menuKey: NavigationMenuKey): boolean =>
    canAccessMenuKey({
      menuKey,
      user,
      matrix: navigationPermissionMatrix
    })

  const studentRouteFallback = (() => {
    const candidates: Array<{ menuKey: NavigationMenuKey; path: string }> = [
      { menuKey: 'student.dashboard', path: '/Schüler_Dashboard' },
      { menuKey: 'student.lernplaene', path: '/Meine Lernpläne' },
      { menuKey: 'student.skillmap', path: '/Skillmap' },
      { menuKey: 'student.classwork', path: '/student/classwork' },
      { menuKey: 'student.klassenbuch', path: '/student/klassenbuch' },
      { menuKey: 'student.dateien', path: '/dateien' },
      { menuKey: 'student.appointments', path: '/appointments/student' }
    ]
    return candidates.find((candidate) => canAccessRouteMenu(candidate.menuKey))?.path || '/dashboard'
  })()

  const withMenuAccess = (menuKey: NavigationMenuKey, element: React.ReactNode) =>
    canAccessRouteMenu(menuKey)
      ? element
      : <Navigate to={isOnlyStudent ? studentRouteFallback : '/dashboard'} replace />

  const showMfaSetupWizard = Boolean(user?.mfa_required)

  return (
    <Layout>
      <MfaSetupWizard
        isOpen={showMfaSetupWizard}
        onOpenSecuritySettings={() => navigate('/settings?tab=security&mfaSetup=totp')}
        onLogout={logout}
      />
      <Routes>
        <Route path="/" element={<Navigate to={isOnlyStudent ? studentRouteFallback : '/dashboard'} replace />} />
        <Route path="/dashboard" element={withMenuAccess('dashboard', <Dashboard />)} />
        <Route path="/students" element={withMenuAccess('schueler.verwaltung', <TeacherRoute><Students /></TeacherRoute>)} />
        <Route path="/students-v2" element={withMenuAccess('schueler.verwaltung', <TeacherRoute><StudentsV2 /></TeacherRoute>)} />
        <Route path="/attendance" element={withMenuAccess('unterricht.anwesenheit', <TeacherRoute><Attendance /></TeacherRoute>)} />
        <Route path="/building-blocks" element={withMenuAccess('sonstiges.bausteine', <TeacherRoute><BuildingBlocks /></TeacherRoute>)} />
        <Route path="/learning-plans" element={<TeacherRoute><LearningPlans /></TeacherRoute>} />
        <Route path="/tasks" element={<TeacherRoute><Tasks /></TeacherRoute>} />
        <Route path="/settings" element={withMenuAccess('settings', <TeacherRoute><Settings /></TeacherRoute>)} />
        <Route path="/device-manager" element={withMenuAccess('admin.geraetemanager', <TeacherRoute><DeviceManager /></TeacherRoute>)} />
        <Route path="/student-files-queue" element={<TeacherRoute><StudentFilesQueue /></TeacherRoute>} />
        <Route path="/email-client" element={withMenuAccess('email.client', <TeacherRoute><EmailClient /></TeacherRoute>)} />
        <Route path="/E-Mail" element={withMenuAccess('email.client', <TeacherRoute><EmailClient /></TeacherRoute>)} />
        <Route path="/dateien" element={withMenuAccess(isOnlyStudent ? 'student.dateien' : 'dateien', <Dateien />)} />
        <Route path="/Akten" element={withMenuAccess('schueler.akten', <TeacherRoute><StudentFiles /></TeacherRoute>)} />
        <Route path="/Akten-Prüfung" element={<TeacherRoute><StudentFilesReviewDashboard /></TeacherRoute>} />
        {/* Teacher/Staff routes */}
        <Route path="/lernbuero" element={withMenuAccess('unterricht.lernbuero', <TeacherRoute><Lernbuero /></TeacherRoute>)} />
        <Route path="/statistics" element={withMenuAccess('schueler.statistik', <TeacherRoute><Statistics /></TeacherRoute>)} />
        <Route path="/student-assessments" element={withMenuAccess('schueler.lernstand_automatik', <TeacherRoute><StudentAssessments /></TeacherRoute>)} />

        {/* Student routes */}
        <Route path="/Schüler_Dashboard" element={withMenuAccess('student.dashboard', <StudentRoute><StudentDashboard /></StudentRoute>)} />
        <Route path="/Meine Lernpläne" element={withMenuAccess('student.lernplaene', <StudentRoute><StudentLearningPlans /></StudentRoute>)} />
        <Route path="/Skillmap" element={withMenuAccess('student.skillmap', <StudentRoute><StudentSkillmap /></StudentRoute>)} />
        <Route path="/appointments/student" element={withMenuAccess('student.appointments', <StudentRoute><StudentAppointments /></StudentRoute>)} />
        <Route path="/curricula" element={withMenuAccess('admin.curricula', <TeacherRoute><Curricula /></TeacherRoute>)} />
        <Route path="/curricula/:id/edit" element={<TeacherRoute><CurriculumEditor /></TeacherRoute>} />
        <Route path="/job-queue" element={<TeacherRoute><JobQueue variant="staff" /></TeacherRoute>} />
        <Route
          path="/admin/job-queue"
          element={withMenuAccess('admin.job_queue',
            <AdminRoute>
              <JobQueue variant="admin" />
            </AdminRoute>
          )}
        />
        <Route path="/sitzordnung" element={<TeacherRoute><Sitzordnung /></TeacherRoute>} />
        <Route path="/courses-groups" element={
          <AdminRoute>
            <Courses />
          </AdminRoute>
        } />
        {/* Neue Route für Schulstruktur */}
        <Route path="/units" element={withMenuAccess('schueler.lerngruppen', <AdminRoute><Units /></AdminRoute>)} />
        {/* Redirects für alte Routen (für Bookmarks) */}
        <Route path="/courses" element={<Navigate to="/units?type=course" replace />} />
        <Route path="/classes" element={<Navigate to="/units?type=class" replace />} />
        <Route path="/groups" element={<Navigate to="/units?type=group" replace />} />
        <Route path="/unterrichten" element={withMenuAccess('unterricht.unterrichten', <TeacherRoute><Unterrichten /></TeacherRoute>)} />
        <Route path="/live-unterricht" element={withMenuAccess('unterricht.unterrichten', <TeacherRoute><LiveLessonPage /></TeacherRoute>)} />
        <Route path="/unterrichten/klassenarbeit" element={withMenuAccess('unterricht.klassenarbeit', <AdminRoute><ClassworkPage /></AdminRoute>)} />
        <Route path="/student/classwork" element={withMenuAccess('student.classwork', <StudentRoute><StudentClassworkPage /></StudentRoute>)} />
        <Route path="/student/klassenbuch" element={withMenuAccess('student.klassenbuch', <StudentRoute><StudentKlassenbuch /></StudentRoute>)} />
        <Route path="/klassenbuch" element={withMenuAccess('unterricht.klassenbuch', <TeacherRoute><Klassenbuch /></TeacherRoute>)} />
        <Route path="/display-steuerung" element={withMenuAccess('unterricht.display', <TeacherRoute><DisplayMirrorPage /></TeacherRoute>)} />
        <Route path="/namen-lernen" element={withMenuAccess('sonstiges.namen_lernen', <NamenLernen />)} />
        <Route path="/namen-lernen/quiz" element={<NamenLernenQuiz />} />
        <Route path="/namen-lernen/snake" element={<NamenLernenSnake />} />
        <Route path="/foto-optimierung" element={
          <AdminRoute>
            <FotoOptimierung />
          </AdminRoute>
        } />
        <Route path="/elternbriefe" element={<Navigate to="/E-Mail" replace state={{ tab: 'elternbriefe' }} />} />
        <Route path="/elternbriefe/neu" element={<MassenmailEditor />} />
        <Route path="/elternbriefe/:id" element={<MassenmailEditor />} />
        <Route path="/elternbriefe/:id/bearbeiten" element={<MassenmailEditor />} />
        <Route path="/massenmail" element={<Navigate to="/elternbriefe" replace />} />
        <Route path="/massenmail/neu" element={<Navigate to="/elternbriefe/neu" replace />} />
        <Route path="/massenmail/:id" element={<Navigate to="/elternbriefe/:id" replace />} />
        <Route path="/massenmail/:id/bearbeiten" element={<Navigate to="/elternbriefe/:id/bearbeiten" replace />} />
        <Route path="/payments" element={withMenuAccess('sonstiges.zahlungen', <TeacherRoute><PaymentManager /></TeacherRoute>)} />
        <Route path="/license" element={withMenuAccess('admin.license', <AdminRoute><License /></AdminRoute>)} />
        <Route path="/performance" element={withMenuAccess('admin.performance', <AdminRoute><PerformanceDashboard /></AdminRoute>)} />
        <Route path="/timetables" element={withMenuAccess('admin.stundenplan', <AdminOrSchulleitungRoute><Timetables /></AdminOrSchulleitungRoute>)} />
        <Route
          path="/timetables/:id/viewer"
          element={withMenuAccess('admin.stundenplan', <AdminOrSchulleitungRoute><TimetableViewer /></AdminOrSchulleitungRoute>)}
        />
        <Route
          path="/Showcase"
          element={withMenuAccess('admin.showcase', <AdminRoute><UiShowcase /></AdminRoute>)}
        />
        <Route path="/admin/kontostand" element={withMenuAccess('admin.kontostand', <AdminOrSchulleitungRoute><AdminHubKontostand /></AdminOrSchulleitungRoute>)} />
        <Route path="/Admin/Kontostand" element={<Navigate to="/admin/kontostand" replace />} />
        <Route
          path="/admin/kalibrierung"
          element={withMenuAccess('admin.acoustic_calibration', <AdminRoute><AdminAcousticCalibration /></AdminRoute>)}
        />
        <Route path="/admin/Kalibrierung" element={<Navigate to="/admin/kalibrierung" replace />} />
        <Route path="/appointments" element={withMenuAccess('teacher.appointments', <TeacherRoute><Appointments /></TeacherRoute>)} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  )
}

export default App
